import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Chip,
  IconButton,
  Avatar,
  Paper,
  TextField,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogContent,
  DialogTitle,
  useTheme,
  useMediaQuery,
  Skeleton,
  Breadcrumbs,
  Link,
  Stack,
  CircularProgress, // Added
  Collapse, // Added
  Alert as MuiAlert, // Added and aliased to avoid conflict if Alert name is used elsewhere
} from '@mui/material';
import {
  Home as HomeIcon,
  AutoAwesome as AutoAwesomeIcon, // Added
  BedOutlined,
  BathtubOutlined,
  SquareFootOutlined,
  LocalParking,
  AcUnit,
  Weekend,
  Kitchen,
  Deck,
  LocationOn,
  DirectionsCar,
  School,
  LocalHospital,
  Restaurant,
  Park,
  ShoppingBag,
  Favorite,
  FavoriteBorder,
  Share,
  Print,
  ArrowBackIos,
  ArrowForwardIos,
  CheckCircle,
  Phone,
  Email,
  Close,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';

import PropertyCarousel from '../components/property/PropertyCarousel';
import { PropertyType } from '../components/property/PropertyCard';
import { mockProperties, mockFeaturedProperties } from '../utils/mockData';
import { summarizeText } from '../utils/aiService'; // Added

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`property-tabpanel-${index}`}
      aria-labelledby={`property-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `property-tab-${index}`,
    'aria-controls': `property-tabpanel-${index}`,
  };
}

const PropertyDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  
  const [property, setProperty] = useState<PropertyType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [similarProperties, setSimilarProperties] = useState<PropertyType[]>([]);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  // State for AI Summary
  const [isSummarized, setIsSummarized] = useState(false);
  const [summaryText, setSummaryText] = useState<string | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);


  useEffect(() => {
    // Simulate API call
    const fetchProperty = async () => {
      setIsLoading(true);
      try {
        // Simulate delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const foundProperty = mockProperties.find(p => p.id === id) || 
                              mockFeaturedProperties.find(p => p.id === id);
        
        if (foundProperty) {
          setProperty(foundProperty);
          
          // Get similar properties (same type or nearby)
          const similar = mockProperties
            .filter(p => p.id !== id && p.type === foundProperty.type)
            .slice(0, 4);
          
          setSimilarProperties(similar);
        } else {
          // Property not found
          navigate('/listings');
        }
      } catch (error) {
        console.error('Error fetching property:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProperty();
    
    // Reset image index when property changes
    setCurrentImageIndex(0);

    // Reset AI summary states when property ID changes
    setIsSummarized(false);
    setSummaryText(null);
    setIsLoadingSummary(false);
    setSummaryError(null);
  }, [id, navigate]);

  const handleToggleSummary = async () => {
    if (isSummarized) {
      setIsSummarized(false);
      setSummaryError(null); // Clear error when showing original
      return;
    }

    setIsLoadingSummary(true);
    setSummaryError(null);
    setSummaryText(null); // Clear previous summary

    try {
      const currentDescription = property?.description || '';
      if (!currentDescription) {
        throw new Error("No description available to summarize.");
      }
      const result = await summarizeText(currentDescription);
      setSummaryText(result);
      setIsSummarized(true);
    } catch (error) {
      console.error("Error summarizing text:", error);
      setSummaryError(error instanceof Error ? error.message : "Failed to generate summary.");
    } finally {
      setIsLoadingSummary(false);
    }
  };

  const handlePrevImage = () => {
    if (!property) return;
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? property.images.length - 1 : prevIndex - 1
    );
  };

  const handleNextImage = () => {
    if (!property) return;
    setCurrentImageIndex((prevIndex) => 
      prevIndex === property.images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFavoriteToggle = () => {
    setIsFavorite(!isFavorite);
    enqueueSnackbar(
      isFavorite ? 'Removed from favorites' : 'Added to favorites',
      { 
        variant: isFavorite ? 'info' : 'success',
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' }
      }
    );
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: property?.title,
        text: `Check out this property: ${property?.title}`,
        url: window.location.href,
      })
      .catch((error) => console.log('Error sharing', error));
    } else {
      // Fallback
      navigator.clipboard.writeText(window.location.href);
      enqueueSnackbar('Link copied to clipboard', { variant: 'success' });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleOpenContactDialog = () => {
    setContactDialogOpen(true);
  };

  const handleCloseContactDialog = () => {
    setContactDialogOpen(false);
  };

  const handleOpenImageDialog = () => {
    setImageDialogOpen(true);
  };

  const handleCloseImageDialog = () => {
    setImageDialogOpen(false);
  };

  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
    handleOpenImageDialog();
  };

  const features = [
    { icon: <LocalParking />, text: 'Parking' },
    { icon: <AcUnit />, text: 'Central AC' },
    { icon: <Weekend />, text: 'Furnished' },
    { icon: <Kitchen />, text: 'Modern Kitchen' },
    { icon: <Deck />, text: 'Balcony' },
  ];

  const amenities = [
    'Swimming Pool',
    'Fitness Center',
    'Parking Garage',
    'Security System',
    'Pet Friendly',
    'Laundry Facilities',
    'High-Speed Internet',
    'Hardwood Floors',
    'Stainless Steel Appliances',
    'Granite Countertops',
    'Walk-in Closets',
    'Ceiling Fans',
  ];

  const contactFormFields = [
    { name: 'name', label: 'Full Name', type: 'text', required: true },
    { name: 'email', label: 'Email Address', type: 'email', required: true },
    { name: 'phone', label: 'Phone Number', type: 'tel', required: false },
    { name: 'message', label: 'Message', type: 'text', multiline: true, rows: 4, required: true },
  ];

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2, mb: 2 }} />
            <Grid container spacing={1}>
              {[1, 2, 3, 4].map((i) => (
                <Grid item xs={3} key={i}>
                  <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={30} width="80%" />
            <Skeleton variant="text" height={30} width="70%" />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (!property) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <HomeIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Property Not Found
        </Typography>
        <Typography variant="body1" paragraph color="text.secondary">
          We couldn't find the property you're looking for.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          component={RouterLink}
          to="/listings"
        >
          Browse Listings
        </Button>
      </Container>
    );
  }

  return (
    <Box>
      {/* Image Gallery Dialog */}
      <Dialog
        fullScreen={isMobile}
        open={imageDialogOpen}
        onClose={handleCloseImageDialog}
        maxWidth="lg"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Property Gallery</Typography>
          <IconButton edge="end" color="inherit" onClick={handleCloseImageDialog} aria-label="close">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: isMobile ? '50vh' : '70vh',
              bgcolor: 'black',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconButton
              onClick={handlePrevImage}
              sx={{
                position: 'absolute',
                left: 16,
                zIndex: 2,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.3)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
              }}
              aria-label="previous image"
            >
              <ArrowBackIos />
            </IconButton>
            
            <Box
              component="img"
              src={property.images[currentImageIndex]}
              alt={`Property image ${currentImageIndex + 1}`}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
            
            <IconButton
              onClick={handleNextImage}
              sx={{
                position: 'absolute',
                right: 16,
                zIndex: 2,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.3)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
              }}
              aria-label="next image"
            >
              <ArrowForwardIos />
            </IconButton>
          </Box>
          
          <Grid container spacing={1} sx={{ mt: 2 }}>
            {property.images.map((image, index) => (
              <Grid item xs={3} sm={2} key={index}>
                <Box
                  component="img"
                  src={image}
                  alt={`Property thumbnail ${index + 1}`}
                  sx={{
                    width: '100%',
                    height: 80,
                    objectFit: 'cover',
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: index === currentImageIndex ? `2px solid ${theme.palette.primary.main}` : 'none',
                    opacity: index === currentImageIndex ? 1 : 0.7,
                    transition: 'all 0.2s',
                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                  onClick={() => setCurrentImageIndex(index)}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Contact Agent Dialog */}
      <Dialog
        open={contactDialogOpen}
        onClose={handleCloseContactDialog}
        aria-labelledby="contact-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="contact-dialog-title" sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Contact Agent</Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleCloseContactDialog}
              aria-label="close"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 1 }}>
            <Avatar 
              src="https://images.pexels.com/photos/5490276/pexels-photo-5490276.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" 
              alt="John Smith"
              sx={{ width: 60, height: 60, mr: 2 }}
            />
            <Box>
              <Typography variant="h6">Sarah Johnson</Typography>
              <Typography variant="body2" color="text.secondary">
                Senior Real Estate Agent
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <Button
                  size="small"
                  startIcon={<Phone />}
                  variant="outlined"
                  href="tel:+1234567890"
                >
                  Call
                </Button>
                <Button
                  size="small"
                  startIcon={<Email />}
                  variant="outlined"
                  href="mailto:sarah@example.com"
                >
                  Email
                </Button>
              </Stack>
            </Box>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          <Typography variant="subtitle1" gutterBottom>
            Inquire about: <strong>{property.title}</strong>
          </Typography>
          
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {contactFormFields.map((field) => (
                <Grid item xs={12} key={field.name}>
                  <TextField
                    name={field.name}
                    label={field.label}
                    type={field.type}
                    required={field.required}
                    fullWidth
                    multiline={field.multiline}
                    rows={field.rows}
                    variant="outlined"
                    defaultValue={field.name === 'message' ? `I'm interested in this property: ${property.title}` : ''}
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ mt: 1 }}
                >
                  Send Message
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
          <Link 
            component={RouterLink} 
            to="/" 
            underline="hover" 
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon fontSize="small" sx={{ mr: 0.5 }} />
            Home
          </Link>
          <Link
            component={RouterLink}
            to="/listings"
            underline="hover"
            color="inherit"
          >
            Listings
          </Link>
          <Typography color="text.primary">{property.title}</Typography>
        </Breadcrumbs>

        <Grid container spacing={4}>
          {/* Main Image Gallery */}
          <Grid item xs={12} md={8}>
            <Box sx={{ position: 'relative' }}>
              {/* Main Image */}
              <Box
                sx={{
                  position: 'relative',
                  height: isMobile ? 300 : 500,
                  borderRadius: 2,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    '& .MuiBox-root': {
                      opacity: 1,
                    },
                  },
                }}
                onClick={handleOpenImageDialog}
              >
                <Box
                  component="img"
                  src={property.images[currentImageIndex]}
                  alt={property.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                
                {/* Overlay with "view all photos" */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    py: 1,
                    px: 2,
                    bgcolor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                  }}
                >
                  <Typography>View All Photos ({property.images.length})</Typography>
                </Box>
                
                {/* Navigation arrows */}
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrevImage();
                  }}
                  sx={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    color: 'white',
                    bgcolor: 'rgba(0,0,0,0.3)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                  }}
                  aria-label="previous image"
                >
                  <ArrowBackIos />
                </IconButton>
                
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextImage();
                  }}
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    color: 'white',
                    bgcolor: 'rgba(0,0,0,0.3)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                  }}
                  aria-label="next image"
                >
                  <ArrowForwardIos />
                </IconButton>
              </Box>
              
              {/* Image Thumbnails */}
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {property.images.slice(0, 4).map((image, index) => (
                  <Grid item xs={3} key={index}>
                    <Box
                      component="img"
                      src={image}
                      alt={`Property thumbnail ${index + 1}`}
                      onClick={() => handleImageClick(index)}
                      sx={{
                        width: '100%',
                        height: 100,
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: index === currentImageIndex ? `2px solid ${theme.palette.primary.main}` : 'none',
                        opacity: index === currentImageIndex ? 1 : 0.7,
                        transition: 'all 0.2s',
                        '&:hover': {
                          opacity: 1,
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>

          {/* Property Info Panel */}
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                {/* Price */}
                <Typography variant="h4" component="div" fontWeight={700} color="primary">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(property.price)}
                  {property.status === 'for rent' && (
                    <Typography variant="body1" component="span" color="text.secondary" sx={{ ml: 1 }}>
                      /month
                    </Typography>
                  )}
                </Typography>
                
                {/* Status Chip */}
                <Chip
                  label={property.status}
                  color={
                    property.status === 'for sale' ? 'primary' :
                    property.status === 'for rent' ? 'secondary' :
                    property.status === 'pending' ? 'warning' : 'default'
                  }
                  size="small"
                  sx={{ mt: 1, mb: 2, textTransform: 'capitalize' }}
                />
                
                {/* Property Details */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    my: 2,
                    p: 2,
                    bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <BedOutlined color="action" />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Beds
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {property.bedrooms}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ textAlign: 'center' }}>
                    <BathtubOutlined color="action" />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Baths
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {property.bathrooms}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ textAlign: 'center' }}>
                    <SquareFootOutlined color="action" />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Sq Ft
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {property.area}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Location */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                  <LocationOn color="action" sx={{ mr: 1, mt: 0.5 }} />
                  <Typography variant="body1">{property.address}</Typography>
                </Box>
                
                <Divider sx={{ my: 3 }} />
                
                {/* Agent Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar 
                    src="https://images.pexels.com/photos/5490276/pexels-photo-5490276.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" 
                    alt="Sarah Johnson"
                    sx={{ width: 60, height: 60, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Sarah Johnson
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Senior Real Estate Agent
                    </Typography>
                  </Box>
                </Box>
                
                {/* Contact Button */}
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  onClick={handleOpenContactDialog}
                  sx={{ mb: 2 }}
                >
                  Contact Agent
                </Button>
                
                {/* Action Buttons */}
                <Stack direction="row" spacing={1} justifyContent="center">
                  <Button
                    startIcon={isFavorite ? <Favorite /> : <FavoriteBorder />}
                    onClick={handleFavoriteToggle}
                    color={isFavorite ? 'error' : 'inherit'}
                    sx={{ fontSize: isSmall ? 12 : 'inherit' }}
                  >
                    {isFavorite ? 'Saved' : 'Save'}
                  </Button>
                  
                  <Button
                    startIcon={<Share />}
                    onClick={handleShare}
                    sx={{ fontSize: isSmall ? 12 : 'inherit' }}
                  >
                    Share
                  </Button>
                  
                  <Button
                    startIcon={<Print />}
                    onClick={handlePrint}
                    sx={{ fontSize: isSmall ? 12 : 'inherit' }}
                  >
                    Print
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Property Information Tabs */}
        <Box sx={{ mt: 6 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="property tabs"
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons={isMobile ? "auto" : false}
            allowScrollButtonsMobile
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                fontWeight: 600,
              },
            }}
          >
            <Tab label="Overview" {...a11yProps(0)} />
            <Tab label="Features & Amenities" {...a11yProps(1)} />
            <Tab label="Location" {...a11yProps(2)} />
            <Tab label="Property History" {...a11yProps(3)} />
          </Tabs>
          
          {/* Tab 1: Overview */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                <Typography variant="h5" gutterBottom fontWeight={700}>
                  {property.title}
                </Typography>
                
                {/* AI Summary Controls and Display */}
                {property.description && property.description.length > 50 && (
                  <Box sx={{ my: 2, display: 'flex', justifyContent: 'flex-end' }}> {/* Align button to the right */}
                    <Button
                      variant="outlined"
                      size="small" // Keep button size consistent
                      onClick={handleToggleSummary}
                      disabled={isLoadingSummary}
                      startIcon={!isSummarized && !isLoadingSummary ? <AutoAwesomeIcon /> : undefined}
                    >
                      {isLoadingSummary ? (
                        <>
                          Generating Summary
                          <CircularProgress size={20} sx={{ ml: 1 }} />
                        </>
                      ) : isSummarized ? "Show Original Description" : "Summarize with AI"}
                    </Button>
                  </Box>
                )}

                {summaryError && !isLoadingSummary && (
                  <MuiAlert severity="error" sx={{ mt: 1, mb: 1 }}>{summaryError}</MuiAlert>
                )}

                <Collapse in={isSummarized && !!summaryText && !isLoadingSummary} sx={{ width: '100%' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mt: 1, // Margin top to space from button
                      mb: 2, // Margin bottom to space from original description
                      bgcolor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[800], // Subtle background
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: theme.shape.borderRadius,
                    }}
                  >
                    <Typography variant="subtitle1" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}> {/* Less prominent than primary.main */}
                      AI Generated Summary
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        {summaryText}
                    </Typography>
                  </Paper>
                </Collapse>
                
                {/* Original Description - ensure it's always rendered for height calculations and then visually adapted */}
                <Box
                    sx={{
                        filter: isSummarized && !!summaryText && !isLoadingSummary && !summaryError ? 'blur(4px)' : 'none', // Slightly more blur
                        opacity: isSummarized && !!summaryText && !isLoadingSummary && !summaryError ? 0.4 : 1, // Slightly more opacity
                        transition: 'filter 0.3s ease-in-out, opacity 0.3s ease-in-out', // Smoother transition
                        userSelect: isSummarized && !!summaryText && !isLoadingSummary && !summaryError ? 'none' : 'auto',
                        pointerEvents: isSummarized && !!summaryText && !isLoadingSummary && !summaryError ? 'none' : 'auto',
                    }}
                >
                    <Typography variant="body1" paragraph>
                    {property.description}
                    </Typography>

                    <Typography variant="body1" paragraph>
                    This stunning {property.type} offers a perfect blend of comfort and style, situated in a prime location with easy access to essential amenities. The spacious layout includes {property.bedrooms} bedrooms and {property.bathrooms} bathrooms, making it an ideal choice for {property.bedrooms > 2 ? 'families' : 'individuals or couples'}.
                    </Typography>

                    <Typography variant="body1" paragraph>
                    The property features high-quality finishes throughout, modern appliances, and thoughtfully designed spaces that maximize both functionality and aesthetics. Ample natural light floods the interior, creating a warm and inviting atmosphere.
                    </Typography>
                </Box>
                
                {/* Key Features */}
                <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mt: 4 }}>
                  Key Features
                </Typography>
                
                <Grid container spacing={2}>
                  {features.map((feature, index) => (
                    <Grid item xs={6} sm={4} key={index}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          bgcolor: theme.palette.mode === 'light' ? 'grey.100' : 'grey.800',
                          height: '100%',
                        }}
                      >
                        <Box sx={{ mr: 1.5, color: 'primary.main' }}>
                          {feature.icon}
                        </Box>
                        <Typography variant="body2">{feature.text}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card elevation={1} sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      Property Details
                    </Typography>
                    
                    <List dense disablePadding>
                      {[
                        { label: 'Property Type', value: property.type },
                        { label: 'Year Built', value: '2018' },
                        { label: 'Lot Size', value: '0.25 acres' },
                        { label: 'Heating', value: 'Forced air' },
                        { label: 'Cooling', value: 'Central air' },
                        { label: 'Parking', value: '2 garage spaces' },
                        { label: 'Property ID', value: property.id.toUpperCase() },
                      ].map((item, index) => (
                        <ListItem
                          key={index}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            py: 1,
                            borderBottom: index !== 6 ? `1px solid ${theme.palette.divider}` : 'none',
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {item.label}
                          </Typography>
                          <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
                            {item.value}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
                
                <Card elevation={1}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      Mortgage Estimate
                    </Typography>
                    
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(property.price * 0.8 * 0.005)}
                      <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 1 }}>
                        /month
                      </Typography>
                    </Typography>
                    
                    <List dense disablePadding>
                      {[
                        { label: 'Down Payment (20%)', value: `$${(property.price * 0.2).toLocaleString()}` },
                        { label: 'Loan Amount', value: `$${(property.price * 0.8).toLocaleString()}` },
                        { label: 'Interest Rate', value: '5.0%' },
                        { label: 'Loan Term', value: '30 years' },
                      ].map((item, index) => (
                        <ListItem
                          key={index}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            py: 1,
                            borderBottom: 
                              index !== 3 ? `1px solid ${theme.palette.divider}` : 'none',
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {item.label}
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {item.value}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                    
                    <Button
                      variant="text"
                      color="primary"
                      fullWidth
                      sx={{ mt: 2 }}
                      component={RouterLink}
                      to="/mortgage-calculator"
                    >
                      Calculate with different terms
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* Tab 2: Features & Amenities */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Interior Features
                </Typography>
                
                <Grid container spacing={2}>
                  {amenities.slice(0, 6).map((amenity, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircle color="primary" fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body1">{amenity}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                
                <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mt: 4 }}>
                  Exterior Features
                </Typography>
                
                <Grid container spacing={2}>
                  {[
                    'Private Yard',
                    'Patio',
                    'Landscaped Garden',
                    'Outdoor Lighting',
                    'Sprinkler System',
                    'Gated Community',
                  ].map((amenity, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircle color="primary" fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body1">{amenity}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Building Amenities
                </Typography>
                
                <Grid container spacing={2}>
                  {amenities.slice(6).map((amenity, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircle color="primary" fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body1">{amenity}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                
                <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mt: 4 }}>
                  Additional Amenities
                </Typography>
                
                <Grid container spacing={2}>
                  {[
                    'Electric Vehicle Charging',
                    'Smart Home Technology',
                    'Concierge Service',
                    'Package Receiving',
                    'Guest Parking',
                    'Storage Space',
                  ].map((amenity, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircle color="primary" fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body1">{amenity}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* Tab 3: Location */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={7}>
                {/* Map placeholder */}
                <Paper
                  sx={{
                    width: '100%',
                    height: 400,
                    bgcolor: theme.palette.mode === 'light' ? 'grey.100' : 'grey.800',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    mb: 3,
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    Interactive Map Would Appear Here
                  </Typography>
                </Paper>
                
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Neighborhood Information
                </Typography>
                
                <Typography variant="body1" paragraph>
                  This property is located in a vibrant neighborhood with a mix of residential and commercial areas. The local community offers a blend of urban convenience and suburban tranquility, making it an ideal location for various lifestyles.
                </Typography>
                
                <Typography variant="body1" paragraph>
                  The area boasts excellent walkability scores, with many essential amenities within walking distance. Public transportation options are readily available, providing easy access to the city center and surrounding areas.
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={5}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Points of Interest
                </Typography>
                
                <List>
                  {[
                    { icon: <DirectionsCar />, name: 'Public Transportation', distance: '0.2 miles' },
                    { icon: <School />, name: 'Lincoln Elementary School', distance: '0.5 miles' },
                    { icon: <LocalHospital />, name: 'City Medical Center', distance: '1.3 miles' },
                    { icon: <Restaurant />, name: 'Downtown Restaurants', distance: '0.7 miles' },
                    { icon: <Park />, name: 'Riverside Park', distance: '0.4 miles' },
                    { icon: <ShoppingBag />, name: 'Metro Shopping Mall', distance: '1.1 miles' },
                  ].map((item, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        py: 1.5,
                        px: 0,
                        borderBottom: index !== 5 ? `1px solid ${theme.palette.divider}` : 'none',
                      }}
                    >
                      <ListItemIcon>
                        <Box sx={{ color: 'primary.main' }}>{item.icon}</Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        secondary={item.distance}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </ListItem>
                  ))}
                </List>
                
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  sx={{ mt: 3 }}
                >
                  Get Directions
                </Button>
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* Tab 4: Property History */}
          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Property History
            </Typography>
            
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                <Paper elevation={0} sx={{ p: 3, bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'grey.900' }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Sale History
                  </Typography>
                  
                  <List>
                    {[
                      { date: 'May 15, 2023', price: property.price, type: 'Listed' },
                      { date: 'January 3, 2020', price: property.price * 0.85, type: 'Sold' },
                      { date: 'December 10, 2019', price: property.price * 0.87, type: 'Listed' },
                      { date: 'April 22, 2015', price: property.price * 0.7, type: 'Sold' },
                    ].map((item, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          px: 0,
                          py: 2,
                          borderBottom: index !== 3 ? `1px solid ${theme.palette.divider}` : 'none',
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={4} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                              {item.date}
                            </Typography>
                          </Grid>
                          <Grid item xs={4} sm={3}>
                            <Typography variant="body2" fontWeight={500}>
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              }).format(item.price)}
                            </Typography>
                          </Grid>
                          <Grid item xs={4} sm={6}>
                            <Chip
                              label={item.type}
                              size="small"
                              color={item.type === 'Listed' ? 'primary' : 'success'}
                              variant="outlined"
                            />
                          </Grid>
                        </Grid>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
                
                <Paper elevation={0} sx={{ p: 3, bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'grey.900', mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Tax History
                  </Typography>
                  
                  <List>
                    {[
                      { year: '2023', value: property.price * 0.65, taxes: property.price * 0.65 * 0.011 },
                      { year: '2022', value: property.price * 0.62, taxes: property.price * 0.62 * 0.011 },
                      { year: '2021', value: property.price * 0.6, taxes: property.price * 0.6 * 0.011 },
                    ].map((item, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          px: 0,
                          py: 2,
                          borderBottom: index !== 2 ? `1px solid ${theme.palette.divider}` : 'none',
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              {item.year}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2" fontWeight={500}>
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              }).format(item.value)}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2" fontWeight={500}>
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              }).format(item.taxes)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      Ownership & Permits
                    </Typography>
                    
                    <List disablePadding>
                      {[
                        { label: 'Current Owner Since', value: 'January 2020' },
                        { label: 'Previous Owners', value: '2 recorded' },
                        { label: 'Building Permits', value: '3 recorded' },
                        { label: 'Last Permit Issued', value: 'March 2022' },
                        { label: 'Zoning', value: 'Residential' },
                      ].map((item, index) => (
                        <ListItem
                          key={index}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            py: 1.5,
                            borderBottom: 
                              index !== 4 ? `1px solid ${theme.palette.divider}` : 'none',
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {item.label}
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {item.value}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Box>

        {/* Similar Properties Section */}
        <Box sx={{ mt: 8 }}>
          <PropertyCarousel
            title="Similar Properties"
            description="You might also be interested in these properties"
            properties={similarProperties}
            viewAllLink="/listings"
          />
        </Box>
      </Container>
    </Box>
  );
};

export default PropertyDetailPage;